import React, { useState } from "react";

import type { EntityAccessLevel, ShareEntity } from "@app/common/types";

interface EditSharingOpts {
  initialBulkAccessLevel: EntityAccessLevel | null;
}

const DEFAULT_OPTS: EditSharingOpts = {
  initialBulkAccessLevel: null,
};

export interface UseEditSharing {
  bulkAccess: EntityAccessLevel;
  isAddingEntities: boolean;
  clearSelectedEntities: () => void;
  onAddEntity: (entity: ShareEntity) => void;
  onChangeBulkAccess: (access: EntityAccessLevel) => void;
  onChangeSelectedEntities: React.Dispatch<React.SetStateAction<ShareEntity[]>>;
  selectedEntities: ShareEntity[];
}

const useEditSharing = (opts = DEFAULT_OPTS): UseEditSharing => {
  const [isAddingEntities, setIsAddingEntities] = useState(false);
  const [selectedEntities, setSelectedEntities] = useState<ShareEntity[]>([]);
  const [bulkAccess, setBulkAccess] = useState<EntityAccessLevel>(
    opts.initialBulkAccessLevel == "editor" ? "editor" : "viewer_full"
  );

  // for use w/ anything that needs to update all the selected entities at once.
  // (e.g., the autocomplete change event passes up the full list of selected entries)
  const handleChangeSelectedEntities: typeof setSelectedEntities = (
    entitiesOrSetStateFn
  ) => {
    setIsAddingEntities(true);
    setSelectedEntities(entitiesOrSetStateFn);
  };

  // for adding a single entity (e.g., the "Quick Add" suggestions)
  const handleAddEntity = (entity: ShareEntity) => {
    setIsAddingEntities(true);
    setSelectedEntities((curr) => [...curr, entity]);
  };

  const clearSelectedEntities = () => {
    setIsAddingEntities(false);
    setSelectedEntities([]);
  };

  return {
    bulkAccess,
    isAddingEntities,
    clearSelectedEntities,
    onAddEntity: handleAddEntity,
    onChangeBulkAccess: setBulkAccess,
    onChangeSelectedEntities: handleChangeSelectedEntities,
    selectedEntities,
  };
};

export default useEditSharing;
